<template>
  <v-sheet
    ref="viewerContainer"
    id="viewersSection"
    class="h-100"
    :class="{ 'viewers-section--industrial': industrialChrome }">
    <v-card class="elevation-0 rounded-2 d-flex flex-column h-100">
      <!-- Enhanced Card Header -->
      <div class="viewer-header" :class="{ 'viewer-header--fullscreen': isFullscreen }">
        <!-- Top Row: Title + Tab Switcher + Close -->
        <div class="viewer-header__top">
          <div class="viewer-header__title">
            <div class="viewer-header__icon-wrapper">
              <v-icon size="20" color="primary">{{ currentViewerIcon }}</v-icon>
            </div>
            <span class="viewer-header__title-text">{{ localViewerTab }}</span>
          </div>

          <div
            v-if="availableViewers.length"
            class="viewer-header__tabs"
            role="tablist"
            aria-label="Viewer type">
            <button
              v-for="tab in availableViewers"
              :key="tab"
              type="button"
              class="viewer-tab-btn"
              :class="{ 'viewer-tab-btn--active': localViewerTab === tab }"
              :disabled="interactionLocked"
              role="tab"
              :aria-selected="localViewerTab === tab ? 'true' : 'false'"
              @click="selectViewerTab(tab)">
              <v-icon size="16" class="viewer-tab-btn__icon">{{ getTabIcon(tab) }}</v-icon>
              <span class="viewer-tab-btn__label">{{ getTabLabel(tab) }}</span>
            </button>
          </div>

          <!-- Window Controls: feature-specific buttons + Expand + Close -->
          <div class="viewer-header__controls">
            <!--
              Phase 2 of TaskBoard viewer migration: feature-specific header controls slot.
              Renders inline with the viewer's expand/close buttons, so consumers (e.g.
              TaskBoard's "X Tasks Isolated" chip) appear in the header chrome instead
              of as floating overlays. Slot scope exposes the active tab and feature
              identifier for conditional rendering. Empty by default — existing consumers
              (Registers, Tags) see no change.
            -->
            <slot name="header-controls" :active-tab="localViewerTab" :feature="feature"></slot>
            <button
              :disabled="localViewerTab == 'Document' || interactionLocked"
              type="button"
              class="viewer-control-btn viewer-control-btn--expand"
              @click="toggleFullscreen"
              title="Expand Viewer">
              <v-icon size="16">mdi-arrow-expand</v-icon>
            </button>
            <button
              :disabled="interactionLocked"
              type="button"
              class="viewer-control-btn viewer-control-btn--close"
              @click="closeViewersSection"
              title="Close Viewer">
              <v-icon size="16">mdi-close</v-icon>
            </button>
          </div>
        </div>

        <!-- Bottom Row: Isolated Tags (only when tags exist) -->
        <div class="viewer-header__bottom" v-if="isolatedTags.length > 0">
          <button class="viewer-tags-btn" @click="dialog = true">
            <span class="viewer-tags-btn__count">{{ isolatedTags.length }}</span>
            <span class="viewer-tags-btn__label"
              >{{ isolatedTags.length == 1 ? 'Tag' : 'Tags' }} Isolated</span
            >
          </button>
        </div>
      </div>
      <v-progress-linear
        v-if="viewerState.isLoading"
        :indeterminate="true"
        color="primary"
        height="3"
        style="border-radius: 0"></v-progress-linear>
      <v-divider></v-divider>
      <v-sheet
        class="viewer-viewport h-100 ma-2"
        style="border-radius: 8px; overflow: overlay; position: relative">
        <v-overlay
          value="true"
          opacity="0.85"
          absolute
          v-show="viewerState.isLoading && localViewerTab === '3D View'"
          class="custom-overlay">
          <v-sheet
            class="d-flex align-center justify-center flex-column"
            width="400"
            color="transparent">
            <p class="text-h4 mb-4 white--text">Loading...</p>
            <v-progress-linear
              class="rounded-pill animate"
              striped
              stream
              height="30"
              :value="viewerLoadingPercent"
              color="primary">
              <template v-slot:default="{ value }">
                <strong class="white--text">{{ Math.ceil(value) }}%</strong>
              </template>
            </v-progress-linear>
          </v-sheet>
        </v-overlay>
        <div
          v-show="localViewerTab === '3D View'"
          id="viewer3d"
          class="w-100"
          style="height: 100%; overflow: overlay; border-radius: 8px"></div>
        <div
          v-show="localViewerTab === '2D View'"
          class="w-100 align-center justify-center"
          style="display: flex; height: 100%; overflow: overlay">
          <v-sheet id="planViewer"></v-sheet>
        </div>
        <div v-show="localViewerTab === '360 View'" id="panorama-viewer-tab" class="w-100 h-100">
          <v-sheet
            color="transparent"
            class="w-100 panorama-overlay"
            :class="{ 'overlay-hidden': !isPanoramaViewerLoading }">
            <v-img
              v-if="lastPanoramaPreviewImage"
              :src="lastPanoramaPreviewImage"
              :style="{ opacity: is360PreviewImageLoaded ? 1 : 0 }"
              @load="is360PreviewImageLoaded = true"
              alt="Preview Image"
              class="preview-image" />
            <div v-if="!lastPanoramaPreviewImage" class="w-100 h-100 white-overlay"></div>
            <div class="glass-overlay d-flex align-center justify-center">
              <envizion-loader :size="48" />
            </div>
          </v-sheet>

          <!-- Photo Sphere Viewer -->
          <PhotoSphere360Viewer
            v-if="usePhotoSphereViewer"
            ref="photoSphere360Viewer"
            :image="currentPanoramaImage"
            :inspects="currentInspects"
            @viewer-ready="onPhotoSphereReady"
            @panorama-loading="onPanoramaLoading"
            @viewer-error="onPhotoSphereError"
            @marker-select="onMarkerSelect"
            @inspect-select="onInspectSelect" />

          <!-- Legacy Pannellum Viewer (shared container for any component) -->
          <div v-else id="pannellum-viewer" style="width: 100%; height: 100%; position: relative">
            <PanoramaCompass
              :get-yaw="getPannellumYaw"
              :north-offset="pannellumNorthOffset"
              :look-at-north="lookAtPannellumNorth"
              :is-interacting="pannellumIsInteracting"
              :selected-image-id="selectedImageId" />
          </div>
        </div>
        <!-- Document Viewer Tab -->
        <div
          v-show="localViewerTab === 'Document'"
          id="document-viewer-container"
          class="w-100 h-100 document-viewer-wrapper">
          <div id="docViewerEmbedded" class="w-100 h-100" style="border-radius: 8px"></div>

          <!-- PDF Pagination Bar (only show for multi-page documents) -->
          <div v-if="documentTotalPages > 1" class="pdf-pagination-bar">
            <v-btn
              icon
              small
              :disabled="documentCurrentPage <= 1"
              @click="previousPage"
              class="pagination-btn">
              <v-icon small>mdi-chevron-left</v-icon>
            </v-btn>
            <span class="page-indicator">{{ documentCurrentPage }} / {{ documentTotalPages }}</span>
            <v-btn
              icon
              small
              :disabled="documentCurrentPage >= documentTotalPages"
              @click="nextPage"
              class="pagination-btn">
              <v-icon small>mdi-chevron-right</v-icon>
            </v-btn>
          </div>
        </div>
        <!--
          Phase 2 of TaskBoard viewer migration: feature-specific overlay slot.
          Renders on top of whichever viewer tab is active. Consumers receive
          the active tab name and feature identifier via slot scope so they can
          conditionally render (e.g. <ViewerTaskColoring v-if="activeTab === '3D View'">).
          Empty by default — existing consumers (Registers, Tags) see no change.
        -->
        <slot name="viewer-overlay" :active-tab="localViewerTab" :feature="feature"></slot>
      </v-sheet>
      <v-sheet
        v-show="viewerState.isLoading && localViewerTab !== '3D View'"
        class="loading-section text-center flex-column">
        <img width="300" src="/img/loading.svg" class="py-5 my-8" style="opacity: 0.54" />
        <p class="font-weight-medium">Loading viewer...</p>
      </v-sheet>
      <v-sheet
        style="height: fit-content; transform: translateY(50%)"
        v-show="noViewerAvailable && !usePhotoSphereViewer"
        class="loading-section text-center flex-column">
        <v-img
          width="300"
          src="/img/void.svg"
          alt="SVG Image"
          class="py-5 my-8"
          style="opacity: 0.54"
          contain></v-img>
        <p class="font-weight-medium">No Model Available</p>
      </v-sheet>
    </v-card>
    <!-- Isolated Tags Dialog -->
    <IsolatedTagsDialog
      v-model="dialog"
      :items="isolatedTags"
      title="Isolated Tags"
      footer-text="Tags currently isolated in the viewer"
      @close="dialog = false" />
  </v-sheet>
</template>

<script>
  import 'pannellum';
  import 'pannellum/src/css/pannellum.css';

  const pannellum = window.pannellum;
  const Autodesk = window.Autodesk;
  import PhotoSphere360Viewer from './PhotoSphere360Viewer.vue';
  import PanoramaCompass from './PanoramaCompass.vue';
  import IsolatedTagsDialog from '@Share/components/IsolatedTagsDialog.vue';
  import { pixelToYawPitch } from '../utils/panorama';
  import { levelPitch, SNAP_DURATION_MS, computeNorthOffset } from '../utils/compass';
  import { axisAngleToEuler } from '@/utils/geometryUtils';
  import { GET_ALL_DOCUMENTS } from '@/utils/attachmentsAPI';
  import HttpService from '@/utils/httpService';
  import { log as debugLog, warn as debugWarn, error as debugError } from '@/utils/debugLogger';
  import { $getAuthHeaders } from '@/utils';
  import { getViewerCurrentPdfPagesCount } from '@/utils/forgeUtils';
  import { fetchDocumentAsObjectUrl, getDocumentId } from '@/utils/documentProxyUtils';
  import { resolveLinkCmlId, applyCmlConditionColorsToDocLinks } from '@/utils/docLinkConditionColors';
  import { getEmptyModelUrn } from '@/scripts/js/forgeviewer';
  import { notifyIfModelNotReady } from '@/scripts/js/modelReadiness';
  import { createPanoramaTagSelectionSeed } from '@/features/Tags/utils/tagMarkerLifecycle';

  export default {
    name: 'viewers-section',
    components: {
      PhotoSphere360Viewer,
      PanoramaCompass,
      IsolatedTagsDialog,
    },
    props: {
      viewerTab: {
        type: String,
        default: '360 View',
      },
      hide360View: {
        type: Boolean,
        default: false,
      },
      usePhotoSphereViewer: {
        type: Boolean,
        default: true, // Default to Photo Sphere Viewer (for AIInspect), set to false for Legacy Pannellum (for CorrosionAnalyser)
      },
      // Phase 2 of TaskBoard viewer migration: feature consumers can restrict
      // which tabs are available. Composes with the existing data-availability
      // logic in `availableViewers` (intersection — a tab shows only if it's
      // both data-available AND in this list). Default preserves existing
      // behavior so other consumers (Registers, Tags) are unaffected.
      enabledTabs: {
        type: Array,
        default: () => ['3D View', '2D View', '360 View', 'Document'],
        validator: (v) =>
          Array.isArray(v) &&
          v.every((t) => ['3D View', '2D View', '360 View', 'Document'].includes(t)),
      },
      // Identifies the consuming feature (e.g. 'task', 'register', 'tag') so
      // slot content can render conditionally. Exposed to the viewer-overlay
      // and viewer-sidebar slot scopes.
      feature: {
        type: String,
        default: null,
      },
      // Report mode (AI Analysis): the consumer supplies its own inspects instead of writing the shared
      // AI_INSPECT/ISOLATED_INSPECTS slot, which a kept-alive AI Inspect page also watches. null = store-driven.
      inspects: {
        type: Array,
        default: null,
      },
      // Opt-in chrome for dense engineering surfaces. Custom Dashboard enables
      // this while every existing consumer retains the current viewer styling.
      industrialChrome: {
        type: Boolean,
        default: false,
      },
      // Consumers can temporarily lock navigation while an irreversible request is in flight.
      // This stays domain-neutral: the consumer owns the reason and the async lifecycle.
      interactionLocked: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        viewerState: {
          isLoading: false,
        },
        dialog: false,
        isFullscreen: false,
        currentPanoramaImage: null,
        currentInspects: [],
        currentTag: null, // Track the current tag being viewed for navigation context
        pannellumViewerInstance: null, // Store legacy Pannellum viewer instance
        pannellumNorthOffset: null, // Current scan's northOffset in degrees; null = not ready (compass hides)
        pannellumIsInteracting: false, // True during pointer-down + 500ms coast; drives rAF gating in PanoramaCompass
        pannellumWheelHandler: null, // Stored reference so wheel listener can be removed on destroy
        pannellumInteractEndTimer: null, // Pending setTimeout for interaction-end coast window
        panoramaHotspotManager: null, // Manager for displaying tag hotspots in Pannellum viewer
        documentViewer: null, // Autodesk viewer instance for document viewing
        // PDF pagination state
        documentCurrentPage: 1,
        documentTotalPages: 1,
        // PDF viewer initialization guards (prevent duplicate viewer instances)
        isInitializingViewer: false, // Prevents concurrent initialization
        currentDocumentId: null, // Tracks currently loaded document ID to prevent duplicate loads
      };
    },
    methods: {
      /**
       * Get icon for the current viewer tab
       */
      getTabIcon(tab) {
        const icons = {
          '3D View': 'mdi-cube-outline',
          '2D View': 'mdi-floor-plan',
          '360 View': 'mdi-image-filter-center-focus',
          Document: 'mdi-file-document-outline',
        };
        return icons[tab] || 'mdi-eye';
      },
      /**
       * Get shortened label for tab buttons
       */
      getTabLabel(tab) {
        const labels = {
          '3D View': '3D',
          '2D View': '2D',
          '360 View': '360°',
          Document: 'Doc',
        };
        return labels[tab] || tab;
      },
      selectViewerTab(tab) {
        if (this.interactionLocked) return;
        this.localViewerTab = tab;
      },
      // ---------------------------------------------------------------------
      // PanoramaCompass adapter — viewer-agnostic wrappers around Pannellum.
      // Passed as props so PanoramaCompass doesn't import any viewer library
      // and can be reused for Photo Sphere Viewer in a follow-up phase with a
      // radians → degrees converter.
      // ---------------------------------------------------------------------
      getPannellumYaw() {
        return this.pannellumViewerInstance ? this.pannellumViewerInstance.getYaw() : null;
      },
      // Round 6 signature: second arg forces pitch-level regardless of magnitude
      // (passed true on double-click, false on single click). Uses the shared
      // levelPitch() helper from compass.js.
      lookAtPannellumNorth(animationMs, forceLevelPitch) {
        if (!this.pannellumViewerInstance || this.pannellumNorthOffset == null) return;
        const viewer = this.pannellumViewerInstance;
        const currentPitch = viewer.getPitch();
        const targetPitch = levelPitch(currentPitch, !!forceLevelPitch);
        const ms = Number.isFinite(animationMs) ? animationMs : SNAP_DURATION_MS;
        // Round 7: Pannellum convention — facing north means yaw = -northOffset,
        // NOT yaw = northOffset. The target yaw is negated so the viewer actually
        // snaps to the physical north direction (matching Pannellum's built-in
        // compass needle).
        const targetYaw = -this.pannellumNorthOffset;
        debugLog('[ViewersSection] snap compass to north', {
          targetPitch,
          targetYaw,
          northOffset: this.pannellumNorthOffset,
          animationMs: ms,
          forceLevelPitch: !!forceLevelPitch,
        });
        viewer.lookAt(targetPitch, targetYaw, viewer.getHfov(), ms);
      },
      updatePhotoSphereViewer(inspects) {
        if (!inspects || inspects.length === 0) return;

        const firstInspect = inspects[0];
        const imageId = firstInspect.imageId;

        // Find the image from the store
        const images = this.$store.getters['AI_INSPECT/IMAGES'];
        const image = images.find((img) => img.id === imageId);

        if (image) {
          this.currentPanoramaImage = image;

          // Prepare inspect data for markers
          this.currentInspects = inspects.map((inspect) => ({
            imageWidth: inspect.imageWidth || 75,
            imageHeight: inspect.imageHeight || 100,
            centerPoint: {
              x: parseInt(inspect.imageXCenterCoordinate),
              y: parseInt(inspect.imageYCenterCoordinate),
            },
            imageXCenterCoordinate: inspect.imageXCenterCoordinate,
            imageYCenterCoordinate: inspect.imageYCenterCoordinate,
            ...inspect,
          }));
        }
      },
      closeViewersSection() {
        if (this.interactionLocked) return;
        this.$store.commit('SET_VIEWER_TYPE', '2d');
        this.$emit('closeViewersSection');
      },
      displayViewer() {
        if (this.localViewerTab === '2D View') {
          this.load2dViewer(this.$store.state.selectedLevel?.urn);
        } else if (this.localViewerTab === '3D View') {
          return this.load3dViewer().catch((err) => {
            debugError('Error displaying 3D viewer', err);
          });
        } else if (this.localViewerTab === '360 View') {
          if (this.usePhotoSphereViewer) {
            this.loadPhotoSphereViewer();
          } else {
            this.$store.dispatch('AI_INSPECT/START_INSPECT_VIEWER');
          }
        } else if (this.localViewerTab === 'Document') {
          // If no activeDocument but tag has P&ID, lazy-load it
          if (!this.$store.state.TAGS?.activeDocument && this.isolatedTagHasPId) {
            this.loadPIdForIsolatedTag();
          } else {
            this.loadDocumentViewer();
          }
        } else {
          debugWarn('ViewersSection', 'displayViewer: Unknown tab', this.localViewerTab);
        }
      },
      /**
       * Lazy-load P&ID document for the currently isolated tag
       */
      async loadPIdForIsolatedTag() {
        const isolatedTags = this.$store.getters['TAGS_REGISTER/GET_ISOLATED_TAGS'] ?? [];
        if (!isolatedTags.length) return;

        const tag = isolatedTags[0];
        const pIdTypeId = this.$store.getters.GET_DOCUMENT_TYPES?.find(
          (t) => t.name === 'P&IDs',
        )?.id;

        if (!pIdTypeId) return;

        this.viewerState.isLoading = true;

        try {
          const { items } = await GET_ALL_DOCUMENTS({
            AssetId: this.$store.state.assetId,
            ForgeTagsIds: [tag.id],
            DocumentTypeIds: [pIdTypeId],
          });

          if (items?.length) {
            this.$store.commit('SET_ACTIVE_DOCUMENT', items[0]);
            this.$store.commit('setTagIdToHighlight', tag.id);
            this.loadDocumentViewer();
          } else {
            this.viewerState.isLoading = false;
          }
        } catch (error) {
          debugError('Error loading P&ID for isolated tag:', error);
          this.viewerState.isLoading = false;
        }
      },
      /**
       * Load the document viewer for PDF/document viewing
       */
      loadDocumentViewer() {
        const activeDocument = this.$store.state.TAGS?.activeDocument;

        if (!activeDocument) {
          debugWarn('ViewersSection', 'loadDocumentViewer: No active document in store - aborting');
          return;
        }

        if (!activeDocument.url) {
          debugWarn('ViewersSection', 'loadDocumentViewer: Active document has no URL', {
            documentId: activeDocument.documentId,
            storageReference: activeDocument.storageReference,
            allKeys: Object.keys(activeDocument),
          });
          return;
        }

        this.initEmbeddedPDFViewer(activeDocument);
      },
      /**
       * Initialize the embedded PDF viewer using Autodesk PDF extension
       * @param {Object} attachment - Document attachment with url, pagesMarkups, etc.
       */
      initEmbeddedPDFViewer(attachment) {
        // GUARD 1: Prevent concurrent initialization (race condition protection)
        // The Autodesk.Viewing.Initializer callback is async, so rapid calls could
        // result in multiple callbacks executing and creating duplicate viewers
        if (this.isInitializingViewer) {
          debugWarn(
            'ViewersSection',
            'initEmbeddedPDFViewer: Already initializing, skipping duplicate call',
          );
          return;
        }

        // GUARD 2: Skip if same document already loaded
        // Uses stable document ID instead of URL (URLs change with SAS tokens)
        const attachmentId = getDocumentId(attachment);
        if (attachmentId && this.currentDocumentId === attachmentId && this.documentViewer) {
          return;
        }

        this.isInitializingViewer = true;
        this.viewerState.isLoading = true;

        const container = document.getElementById('docViewerEmbedded');
        if (!container) {
          debugError('Document viewer container not found');
          this.viewerState.isLoading = false;
          this.isInitializingViewer = false;
          return;
        }

        // Dispose existing viewer properly before creating new one
        if (this.documentViewer) {
          this.documentViewer.finish();
          this.documentViewer = null;
        }

        // CRITICAL FIX: Clear container DOM before creating new viewer
        // This prevents orphaned viewer elements from accumulating
        container.innerHTML = '';

        Autodesk.Viewing.Initializer({}, () => {
          // Double-check guard is still valid (async callback may execute after cancellation)
          if (!this.isInitializingViewer) {
            debugWarn('ViewersSection', 'Initializer callback aborted - init was cancelled');
            return;
          }

          // Auth headers required for non-document Forge API requests (3D models, attachments, etc.)
          Autodesk.Viewing.endpoint.HTTP_REQUEST_HEADERS = $getAuthHeaders();

          this.documentViewer = new Autodesk.Viewing.Private.GuiViewer3D(container, {
            extensions: ['Autodesk.DocumentBrowser'],
          });
          this.documentViewer.setTheme('light-theme');
          this.documentViewer.start();

          // Reset pagination state before loading new document
          this.documentCurrentPage = 1;
          this.documentTotalPages = 1;

          this.documentViewer.loadExtension('Autodesk.PDF').then(async () => {
            // Construct direct blob URL with fresh SAS token
            const blobUrl = await fetchDocumentAsObjectUrl(attachment, { strict: true });
            if (!blobUrl) {
              debugError(
                'ViewersSection',
                'initEmbeddedPDFViewer: Cannot fetch document - no storageReference',
              );
              this.viewerState.isLoading = false;
              this.isInitializingViewer = false;
              return;
            }

            this.documentViewer.loadModel(
              blobUrl,
              { page: 1 },
              (model) => {
                // Track current document ID to prevent duplicate loads (stable identity)
                this.currentDocumentId = getDocumentId(attachment);

                this.documentViewer.setBackgroundColor(255, 255, 255, 255, 255, 255);
                this.viewerState.isLoading = false;
                this.isInitializingViewer = false; // Clear guard on success

                // Extract total page count using the proper Forge API method
                const pagesCount = getViewerCurrentPdfPagesCount(this.documentViewer);
                if (pagesCount && pagesCount > 0) {
                  this.documentTotalPages = pagesCount;
                } else if (attachment.pagesMarkups && attachment.pagesMarkups.length > 0) {
                  // Fallback for P&ID documents with markup pages
                  this.documentTotalPages = attachment.pagesMarkups.length;
                }

                // Hide the toolbar
                const toolbar = container.querySelector('#guiviewer3d-toolbar');
                if (toolbar) toolbar.style.display = 'none';

                // Load markups if present (for P&ID documents)
                this.loadDocumentMarkups(attachment);

                // Set up reverse zoom direction
                this.documentViewer.navigation.setReverseZoomDirection(true);
              },
              (err) => {
                debugError('ViewersSection', 'Failed to load PDF model:', err);
                this.viewerState.isLoading = false;
                this.isInitializingViewer = false; // Clear guard on error
              },
            );
          });
        });
      },
      /**
       * Load P&ID markups and links onto the document viewer
       * @param {Object} attachment - Document attachment with pagesMarkups
       * @param {number} pageNumber - The page number to load markups for (1-indexed, default: 1)
       */
      loadDocumentMarkups(attachment, pageNumber = 1) {
        if (!attachment.pagesMarkups || !this.documentViewer) return;

        const page = attachment.pagesMarkups.find((o) => o.pageId == pageNumber);
        if (!page) return;

        // Apply rotation if present
        if (page.rotation) {
          const { rotateViewerCamera } = require('@/utils/forgeUtils');
          rotateViewerCamera(this.documentViewer, page.rotation);
        }

        // Remove any existing markup links
        const container = this.documentViewer.container;
        if (container) {
          container.querySelectorAll('.markup-link').forEach((el) => el.remove());
        }
        // Add markup links if present
        if (page.links) {
          const forgeService = this.$store.state.forgeService;
          const highlightedId = this.$store.getters['getTagIdToHighlight'];

          page.links.forEach((link) => {
            const classes =
              highlightedId && link.tagId == highlightedId
                ? 'markup-link selected-markup-link'
                : 'markup-link';

            const iconOptions = {
              position: link.position,
              width: link.width,
              height: link.height,
              rotation: link.rotation,
              differences: null,
              title: link.name,
              id: link.id ?? link.linkedAttachmentId,
              className: classes,
              forgeViewer: this.documentViewer,
              metadata: link.tagId,
              linkType: link.type,
              cmlId: resolveLinkCmlId(link),
              onClick: (e) => {
                e.stopPropagation();
                e.preventDefault();
                link.element = e.srcElement;
                this.$store.commit('setPreviewingLinkElement', e.srcElement);
                this.$store.commit('setPdfViewerPreviewingLink', link);
              },
              addContent: true,
              removeTildeSign: true,
            };

            if (forgeService) {
              forgeService.create3DIconToViewer(iconOptions);
            }
          });

          // Patch CML/TML links to their condition color (render-then-patch;
          // fails open to link-type color).
          applyCmlConditionColorsToDocLinks(container);

          this.$store.commit('setTagIdToHighlight', null);
        }
      },
      /**
       * Navigate to the previous page in the PDF document
       */
      previousPage() {
        if (this.documentCurrentPage > 1) {
          this.documentCurrentPage--;
          this.goToPage(this.documentCurrentPage);
        }
      },
      /**
       * Navigate to the next page in the PDF document
       */
      nextPage() {
        if (this.documentCurrentPage < this.documentTotalPages) {
          this.documentCurrentPage++;
          this.goToPage(this.documentCurrentPage);
        }
      },
      /**
       * Navigate to a specific page in the PDF document
       * @param {number} pageNumber - The page number to navigate to (1-indexed)
       */
      async goToPage(pageNumber) {
        if (!this.documentViewer) return;

        // Get the active document to reload markups for the new page
        const activeDocument = this.$store.state.TAGS?.activeDocument;

        // CRITICAL FIX: Unload current model before loading new page
        // Without this, loadModel() creates a new nested .adsk-viewing-viewer inside the existing one,
        // causing duplicate viewer instances and sizing/memory issues
        if (this.documentViewer.model) {
          this.documentViewer.unloadModel();
        }

        // Construct direct blob URL with fresh SAS token
        const blobUrl = await fetchDocumentAsObjectUrl(activeDocument, { strict: true });
        if (!blobUrl) {
          debugWarn('ViewersSection', 'goToPage: Cannot fetch document - no storageReference');
          return;
        }
        this.documentViewer.loadModel(
          blobUrl,
          { page: pageNumber },
          () => {
            // Reload markups for the new page if available
            if (activeDocument) {
              this.loadDocumentMarkups(activeDocument, pageNumber);
            }
          },
          (err) => {
            debugError('ViewersSection', 'Failed to load page:', pageNumber, err);
          },
        );
      },
      loadPhotoSphereViewer() {
        // Photo Sphere Viewer will be loaded via props
        // This method can be used for additional initialization if needed
      },
      // Report mode: rotate the 360 viewer to one inspect (row click → polygon).
      focusInspect(inspect) {
        const viewer = this.$refs.photoSphere360Viewer;
        if (viewer && typeof viewer.focusInspect === 'function') viewer.focusInspect(inspect);
      },
      onPhotoSphereReady(viewer) {
        this.$store.commit('AI_INSPECT/SET_IS_PANORAMA_VIEWER_LOADING', false);
        this.$emit('panorama-ready', viewer);
      },
      onPanoramaLoading(loading) {
        this.$store.commit('AI_INSPECT/SET_IS_PANORAMA_VIEWER_LOADING', loading);
      },
      onPhotoSphereError(error) {
        debugError('Photo Sphere Viewer error:', error);
        this.$store.commit('AI_INSPECT/SET_IS_PANORAMA_VIEWER_LOADING', false);
      },
      onMarkerSelect(marker) {
        // Marker selection handler - reserved for future use
      },
      onInspectSelect(inspect) {
        this.$emit('inspect-selected', inspect);
      },
      async initialize2dViewer(urn) {
        if (!this.planViewer) {
          this.$store.state.assetId = this.$route.params.id;
        }

        this.viewerState.isLoading = true;

        return this.$store
          .dispatch('START_VIEWER_2D', urn)
          .then((newViewer) => {
            document.getElementById('forgePlanViewer')?.remove();

            let parentNode = document.getElementById('planViewer');

            if (!parentNode) return newViewer;

            if (parentNode?.firstChild) {
              parentNode.insertBefore(this.pageView2d, parentNode.firstChild);
            } else {
              parentNode?.appendChild(this.pageView2d);
            }

            return newViewer;
          })
          .catch((error) => {
            debugError('Error starting viewer', error);
          })
          .finally((result) => {
            this.viewerState.isLoading = false;
            return result;
          });
      },
      setupViewerCallback(viewer) {
        return new Promise((resolve, reject) => {
          try {
            let callback = () => {
              setTimeout(() => {
                resolve('Viewer is ready');
                viewer.removeEventListener(Autodesk.Viewing.GEOMETRY_LOADED_EVENT, callback);
              }, 100);
            };

            if (viewer?.model?.isLoadDone()) {
              callback();
            } else {
              viewer.addEventListener(Autodesk.Viewing.GEOMETRY_LOADED_EVENT, callback);
            }
          } catch (error) {
            reject(error);
          }
        });
      },
      load2dViewer(urn) {
        return this.initialize2dViewer(urn);
      },
      load3dViewer(options = {}) {
        return this.initialize3dViewer(options);
      },
      /**
       * Load legacy Pannellum 360 viewer
       * @param {Object} image - 360 image object with imageUrl, lockedYaw, lockedPitch, etc.
       * @param {Object|null} tag - Single tag object with centerPoint (2D) or point (3D) coordinates, or null
       * @param {String} viewerId - DOM element ID (default: 'pannellum-viewer')
       * @returns {Promise} Resolves when viewer is loaded
       */
      loadPannellumViewer(image, tag = null, viewerId = 'pannellum-viewer') {
        return new Promise((resolve, reject) => {
          try {
            // Store current tag for navigation context
            this.currentTag = tag;

            // Check if viewer already exists and is showing the same image
            if (
              this.pannellumViewerInstance &&
              this.pannellumViewerInstance.forgeImage?.id === image.id
            ) {
              // Viewer already showing this image, just zoom to tag if provided
              if (tag) {
                this.zoomPannellumToTag(tag, image);
              }
              resolve(this.pannellumViewerInstance);
              return;
            }

            this.viewerState.isLoading = true;

            if (!image || !image.imageUrl) {
              this.$dialog?.notify?.warning('No 360 image found');
              this.viewerState.isLoading = false;
              reject(new Error('No image provided'));
              return;
            }

            // Wait for DOM to be ready
            this.$nextTick(async () => {
              const container = document.getElementById(viewerId);

              if (!container) {
                debugError(`Pannellum container #${viewerId} not found`);
                this.viewerState.isLoading = false;
                reject(new Error('Container not found'));
                return;
              }

              // Destroy existing viewer if any
              if (this.pannellumViewerInstance) {
                // Remove wheel listener from previous mount before destroying (prevents leak on scan switch)
                if (this.pannellumWheelHandler) {
                  const prevContainer = document.getElementById(viewerId);
                  if (prevContainer) {
                    prevContainer.removeEventListener('wheel', this.pannellumWheelHandler);
                  }
                  this.pannellumWheelHandler = null;
                }
                if (this.pannellumInteractEndTimer != null) {
                  clearTimeout(this.pannellumInteractEndTimer);
                  this.pannellumInteractEndTimer = null;
                }
                this.pannellumViewerInstance.destroy();
                this.pannellumViewerInstance = null;
                // Reset compass state so overlay hides until the new scan's northOffset is computed
                this.pannellumNorthOffset = null;
                this.pannellumIsInteracting = false;
              }

              // Calculate horizon angles
              if (!image.rotationAngle) image.rotationAngle = Math.abs(image.lockedYaw);
              let horizonRoll = 0,
                horizonPitch = 0;

              if (image.rotationAngle && image.rotationX && image.rotationY && image.rotationZ) {
                const rotation = this.axisAngleToEulerWrapper(
                  image.rotationX,
                  image.rotationY,
                  image.rotationZ,
                  image.rotationAngle,
                );
                horizonPitch = rotation.y;
                horizonRoll = -rotation.x;
              } else if (image.scanType === 'geoslam') {
                horizonRoll = image.rotationX;
                horizonPitch = image.rotationY;
              }

              const url = image.lowResUrl ?? image.imageUrl;

              // Configure Pannellum viewer
              const config = {
                type: image.tilesGenerated && image.highResolution ? 'multires' : 'equirectangular',
                autoLoad: true,
                yaw: image.lockedYaw,
                pitch: image.lockedPitch,
                horizonPitch: horizonPitch,
                horizonRoll: horizonRoll,
                // Built-in compass disabled; PanoramaCompass overlay renders our own informing
                // compass (N/E/S/W labels, heading readout, click-to-snap-north) over this container.
                compass: false,
                northOffset: computeNorthOffset(image.lockedYaw, image.lockedForgeDirection),
                sceneFadeDuration: 1000,
                hfov: 50,
                minHfov: 10,
                maxHfov: 120,
              };

              // Add panorama or multiRes based on image type
              if (image.tilesGenerated && image.highResolution) {
                const multiResConfig = JSON.parse(image.multiResConfig)?.multiRes ?? {
                  basePath: image.tilesBaseUrl,
                  path: '/%l/%s%y_%x',
                  fallbackPath: '/fallback/%s',
                  extension: 'jpg',
                  tileResolution: 512,
                  maxLevel: 5,
                  cubeResolution: 6456,
                };

                // Get container-level SAS token for 360 tiles
                // This ensures proper URL construction: basePath/level/tile.jpg?sasToken
                let tilesBaseUrl = image.tilesBaseUrl;
                let sasToken = '';

                // Check if this is an Azure blob URL that needs signing
                if (
                  tilesBaseUrl &&
                  (tilesBaseUrl.includes('blob.core.windows.net') ||
                    tilesBaseUrl.includes('127.0.0.1:10000'))
                ) {
                  try {
                    const response = await HttpService.get({
                      url: '/api/BlobStorage/sign360TilesFolder',
                      params: { tilesBaseUrl },
                    });
                    if (response.data) {
                      tilesBaseUrl = response.data.baseUrl;
                      sasToken = response.data.sasToken || '';
                    }
                  } catch (err) {
                    debugWarn('Failed to sign 360 tiles URL, using original:', err);
                  }
                }

                multiResConfig.basePath = tilesBaseUrl;

                // Append SAS token to extension so Pannellum constructs URLs correctly:
                // basePath + path + '.' + extension => .../1/f0_0.jpg?sv=...&sig=...
                if (sasToken) {
                  multiResConfig.extension = `${multiResConfig.extension || 'jpg'}?${sasToken}`;
                }

                config.multiRes = multiResConfig;
              } else {
                config.panorama = url;
              }

              // Initialize Pannellum viewer
              this.pannellumViewerInstance = pannellum.viewer(viewerId, config);
              this.pannellumViewerInstance.forgeImage = image;

              // Persist the northOffset so the PanoramaCompass overlay can read it via adapter.
              // Uses the same formula as the config above to stay in sync.
              this.pannellumNorthOffset = computeNorthOffset(
                image.lockedYaw,
                image.lockedForgeDirection,
              );
              debugLog(
                '[ViewersSection] Pannellum compass northOffset set to',
                this.pannellumNorthOffset,
              );

              // Wire interaction gating — PanoramaCompass's rAF loop only runs while this flag is true,
              // so the compass costs nothing when the viewer is idle.
              const onInteractStart = () => {
                if (this.pannellumInteractEndTimer != null) {
                  clearTimeout(this.pannellumInteractEndTimer);
                  this.pannellumInteractEndTimer = null;
                }
                this.pannellumIsInteracting = true;
              };
              const onInteractEnd = () => {
                // 500ms coast window captures inertial drift after pointer release (Pannellum has subtle
                // inertia even when `draggable` defaults apply) so the compass lands on the final yaw.
                if (this.pannellumInteractEndTimer != null) {
                  clearTimeout(this.pannellumInteractEndTimer);
                }
                this.pannellumInteractEndTimer = setTimeout(() => {
                  this.pannellumIsInteracting = false;
                  this.pannellumInteractEndTimer = null;
                }, 500);
              };
              this.pannellumViewerInstance.on('mousedown', onInteractStart);
              this.pannellumViewerInstance.on('touchstart', onInteractStart);
              this.pannellumViewerInstance.on('mouseup', onInteractEnd);
              this.pannellumViewerInstance.on('touchend', onInteractEnd);

              // Mousewheel zoom isn't covered by Pannellum's down/up events. Bind via the DOM element
              // and store the listener reference so it can be removed on the next scan switch.
              this.pannellumWheelHandler = () => {
                onInteractStart();
                onInteractEnd();
              };
              const pannellumContainer = document.getElementById(viewerId);
              if (pannellumContainer) {
                pannellumContainer.addEventListener('wheel', this.pannellumWheelHandler, {
                  passive: true,
                });
              }

              // Track if load event was handled
              let loadHandled = false;

              // Handle viewer load event
              this.pannellumViewerInstance.on('load', () => {
                if (loadHandled) return;
                loadHandled = true;

                this.viewerState.isLoading = false;

                // Add 360 image navigation hotspots
                this.add360ImageHotspots();

                // Zoom to tag if provided
                if (tag) {
                  this.zoomPannellumToTag(tag, image);
                }

                resolve(this.pannellumViewerInstance);
              });

              this.pannellumViewerInstance.on('error', (err) => {
                debugError('Pannellum viewer error:', err);
                this.viewerState.isLoading = false;
                reject(err);
              });

              // Fallback: Poll to check if viewer is loaded (in case 'load' event doesn't fire)
              const checkLoadedInterval = setInterval(() => {
                if (loadHandled) {
                  clearInterval(checkLoadedInterval);
                  return;
                }

                if (this.pannellumViewerInstance?.isLoaded()) {
                  loadHandled = true;
                  clearInterval(checkLoadedInterval);
                  this.viewerState.isLoading = false;

                  // Add 360 image navigation hotspots
                  this.add360ImageHotspots();

                  // Zoom to tag if provided
                  if (tag) {
                    this.zoomPannellumToTag(tag, image);
                  }

                  resolve(this.pannellumViewerInstance);
                }
              }, 500);

              // Clear interval after 30 seconds to prevent memory leak
              setTimeout(() => {
                if (!loadHandled) {
                  clearInterval(checkLoadedInterval);
                  this.viewerState.isLoading = false;
                  debugWarn('Pannellum viewer load timeout');
                  resolve(this.pannellumViewerInstance);
                }
              }, 30000);
            });
          } catch (error) {
            debugError('Error loading Pannellum viewer:', error);
            this.viewerState.isLoading = false;
            reject(error);
          }
        });
      },
      async initialize3dViewer({ pointCloudOnly = false, propagateErrors = false } = {}) {
        if (!this.viewer) {
          this.$store.state.assetId = this.$route.params.id;
          this.viewerState.isLoading = true;
        } else {
          this.forgeService.resetThemingColors();
        }

        let area = this.$store.getters['AREAS/GET_SELECTED_AREA'];

        const customUrn = pointCloudOnly ? getEmptyModelUrn() : null;

        try {
          await this.$store.dispatch('START_VIEWER_3D', {
            area,
            customUrn,
            pointCloudOnly,
          });
          document.getElementById('forgeViewer')?.remove();

          const parentNode = document.getElementById('viewer3d');
          if (!parentNode) throw new Error('3D viewer host not found.');

          if (parentNode.firstChild) {
            parentNode.insertBefore(this.pageView3d, parentNode.firstChild);
          } else {
            parentNode.appendChild(this.pageView3d);
          }

          await this.setupViewerCallback(this.viewer);
          return this.viewer;
        } catch (err) {
          debugError('Error starting viewer', err);
          if (propagateErrors) throw err;
          notifyIfModelNotReady(err, this.$dialog);
          return undefined;
        } finally {
          this.viewerState.isLoading = false;
        }
      },
      /**
       * Capture inline 360° state at expand click time (image, current tag,
       * camera angles) and write the bridge Vuex state (selectedImage,
       * tagIdToHighlight) that pageView.vue's fullscreen 360° flow reads.
       *
       * Returns the camera values plus an immutable tag selection seed for the
       * caller to forward via metadata, or null when not on the 360° tab or no
       * Pannellum scan is loaded (e.g. PhotoSphere-using consumers like AI
       * Inspect — those fall back to the existing fullscreen path).
       *
       * Bridge state is written ONLY here, ONLY at expand click — never during
       * inline scan switching, to keep the inline flow free of forgeService /
       * linkViewers / Cropping3DExtension side effects.
       */
      _collectPanoramaTransferState() {
        if (this.localViewerTab !== '360 View') return null;
        if (!this.pannellumViewerInstance) return null;

        // Pannellum stashes the loaded image on the viewer instance via
        // loadPannellumViewer line `this.pannellumViewerInstance.forgeImage = image`.
        const image = this.pannellumViewerInstance.forgeImage;
        if (!image) return null;

        const yaw = this.pannellumViewerInstance.getYaw();
        const pitch = this.pannellumViewerInstance.getPitch();
        const hfov = this.pannellumViewerInstance.getHfov();

        debugLog(
          '[ViewersSection] panorama transfer captured: image=' +
            (image.name ?? 'unknown') +
            `, yaw=${yaw}, pitch=${pitch}, hfov=${hfov}`,
        );

        // Bridge to fullscreen pageView.vue's startView and forgeService.
        this.$store.commit('SET_SELECTED_IMAGE', image);
        if (this.currentTag?.id) {
          this.$store.commit('setTagIdToHighlight', this.currentTag.id);
        }

        const selectionSeed = this.currentTag
          ? createPanoramaTagSelectionSeed({
              tag: this.currentTag,
              facilityId: this.$store.state.assetId,
              scanId: image.id,
            })
          : null;

        return { yaw, pitch, hfov, selectionSeed };
      },
      toggleFullscreen() {
        if (this.interactionLocked) return;
        const panoramaTransfer = this._collectPanoramaTransferState();

        if (this.$listeners.expand) {
          this.$emit('expand', {
            activeTab: this.localViewerTab,
            feature: this.feature,
            panoramaTransfer,
          });
          return;
        }

        let viewerType = {
          '360 View': '360',
          '2D View': '2d',
          '3D View': '3d',
        };

        let isAI = this.$route.name === 'ai-inspect' || this.$route.name === 'default';

        this.$store.dispatch('VIEWERS_MODE/START_VIEWER_MODE', {
          mode: 'dashboard-fullscreen',
          viewer: viewerType[this.localViewerTab],
          button: 'Back to Dashboard',
          action: isAI ? 'open-inspection-tab' : 'show-tags-in-inspection-tab',
          metadata: isAI ? this.isolatedTags : this.isolatedInspections,
          panoramaTransfer,
        });
      },
      /**
       * Convert pixel coordinates to yaw/pitch angles for Pannellum viewer
       */
      pixelToYawPitchWrapper(x, y, imageWidth, imageHeight) {
        return pixelToYawPitch(x, y, imageWidth, imageHeight);
      },
      /**
       * Convert axis angle to Euler angles for horizon correction
       */
      axisAngleToEulerWrapper(x, y, z, angle) {
        return axisAngleToEuler(x, y, z, angle);
      },
      /**
       * Convert 3D world coordinates to yaw/pitch angles for Pannellum viewer
       * Uses the same algorithm as forgeService.zoomToPointIn360Viewer
       * @param {THREE.Vector3} point3D - 3D point in world space
       * @param {Object} image - 360 image object with forgeCameraPosition
       * @returns {{yaw: number, pitch: number}} Yaw and pitch angles
       */
      convertPoint3DToYawPitch(point3D, image) {
        const THREE = window.THREE;

        if (!image.forgeCameraPosition) {
          debugWarn('Image missing forgeCameraPosition for 3D conversion');
          return null;
        }

        // Calculate direction vector from camera to tag
        const cameraPosition = new THREE.Vector3(
          image.forgeCameraPosition.x,
          image.forgeCameraPosition.y,
          image.forgeCameraPosition.z,
        );

        const direction = new THREE.Vector3();
        direction.subVectors(point3D, cameraPosition).normalize();

        // Use forgeService's conversion method (same as zoomToPointIn360Viewer)
        const forgeService = this.$store.state.forgeService;
        if (!forgeService) {
          debugWarn('forgeService not available for 3D conversion');
          return null;
        }

        return forgeService.getNewYawAndPitch(image, direction);
      },
      /**
       * Add 360 image navigation hotspots to the Pannellum viewer
       * Shows nearby 360 scans that users can navigate to
       */
      add360ImageHotspots() {
        if (!this.pannellumViewerInstance) return;

        const THREE = window.THREE;
        const currentImage = this.pannellumViewerInstance.forgeImage;
        if (!currentImage) return;

        // Get all images and remove existing hotspots
        const allImages = this.$store.state.projects.flatMap((o) => o.images);
        allImages.forEach((img) => {
          try {
            this.pannellumViewerInstance.removeHotSpot(img.name);
          } catch (e) {
            // Hotspot doesn't exist, ignore error
          }
        });

        const forgeService = this.$store.state.forgeService;
        if (!forgeService) return;

        const unitScale = this.$store.state.unitScale ?? 1.0;
        const maxDistance = 3;

        let nearImages = allImages;

        // Filter images based on distance from tag point (if tag exists)
        if (this.currentTag && this.currentTag.point) {
          const tagPoint = new THREE.Vector3(
            this.currentTag.point.x,
            this.currentTag.point.y,
            this.currentTag.point.z,
          );

          nearImages = nearImages.filter((img) => {
            if (img === currentImage || img.name === currentImage.name) return false;

            // Calculate distance from tag point to image camera position
            const distance = img.forgeCameraPosition.distanceTo(tagPoint);
            // Apply unit scale and check if within 3m
            return distance * unitScale <= maxDistance;
          });
        } else {
          // Fallback: use visibility range from current image position if no tag.
          // forgeCameraPosition is viewer-space (metres), stored as navigation.getPosition()
          // + globalOffset — so compare directly against the user's metre setting.
          const visibilityRange =
            this.$store.state.userSettings.displaySettings.getVisibilityRange();
          nearImages = nearImages.filter(
            (img) =>
              img != currentImage &&
              img.name != currentImage.name &&
              img.forgeCameraPosition.distanceTo(currentImage.forgeCameraPosition) <
                visibilityRange,
          );
        }

        if (nearImages.length === 0) return;

        // Add hotspot for each nearby image
        nearImages.forEach((nearImage) => {
          // Validate that the image has a valid camera position
          if (!nearImage.forgeCameraPosition) {
            debugWarn(`Image ${nearImage.name} missing forgeCameraPosition`);
            return;
          }

          const direction = new THREE.Vector3();
          direction
            .subVectors(nearImage.forgeCameraPosition, currentImage.forgeCameraPosition)
            .normalize();
          const yawPitch = forgeService.getNewYawAndPitch(currentImage, direction);

          // Validate yawPitch before adding hotspot
          if (!yawPitch || yawPitch.pitch == null || yawPitch.yaw == null) {
            debugWarn(`Invalid yawPitch for image ${nearImage.name}:`, yawPitch);
            return;
          }

          const hotspot = {
            pitch: yawPitch.pitch,
            yaw: yawPitch.yaw,
            type: 'info',
            cssClass: 'custom-hotspot',
            createTooltipFunc: this.create360ImageTooltip,
            createTooltipArgs: { image: nearImage, currentImage: currentImage },
          };

          this.pannellumViewerInstance.addHotSpot(hotspot);
        });
      },
      /**
       * Creates tooltip for 360 image navigation hotspots
       */
      create360ImageTooltip(hotSpotDiv, args) {
        const imagePath = require('@/images/tags-icons/image360.png');

        hotSpotDiv.classList.add('custom-tooltip');
        const icon = document.createElement('img');
        icon.className = `person-icon ${args.image.scanType}-icon`;
        icon.src = imagePath;
        hotSpotDiv.appendChild(icon);

        // Calculate icon size based on distance
        const devicePixelRatio = window.devicePixelRatio;
        const unitScale = this.$store.state.unitScale ?? 1.0;
        const scaleFactor = 1 / unitScale;
        const distanceFromCamera =
          args.image.forgeCameraPosition.distanceTo(args.currentImage.forgeCameraPosition) *
          scaleFactor;

        let iconSize = 20.0 * (20.0 / (distanceFromCamera + 1.0)) * scaleFactor;
        if (iconSize < 20) iconSize = 20;
        if (iconSize > 1024.0) iconSize = 1024.0;
        iconSize /= devicePixelRatio;

        icon.style.width = iconSize + 'px';
        icon.style.height = iconSize + 'px';

        // Double-click to navigate to this 360 image
        icon.addEventListener('dblclick', () => {
          this.$store.commit('hideThumbnailPopup', '360');
          // Navigate to the new 360 image while maintaining the current tag context
          this.loadPannellumViewer(args.image, this.currentTag, 'pannellum-viewer');
        });

        // Show tooltip on hover
        icon.addEventListener('mouseover', () => {
          const iconBoundary = icon.getBoundingClientRect();
          const width = iconBoundary.width || 20;
          const hotSpotDivStyle = window.getComputedStyle(hotSpotDiv);
          const matrix = new window.WebKitCSSMatrix(hotSpotDivStyle.transform);

          const text = args.image.name;
          const font = '16px ArtifaktElement';
          const canvas = document.createElement('canvas');
          const context = canvas.getContext('2d');
          context.font = font;
          const offset = context.measureText(text);
          const textWidth = offset.width > 200 ? offset.width : 200;

          this.$store.commit('setTooltipObject', {
            title: args.image.name,
            position: {
              left: matrix.m41,
              top: matrix.m42 - 40,
            },
            margin: width / 2 - (textWidth + 20) / 2,
          });
        });

        icon.addEventListener('mouseout', () => {
          this.$store.commit('setTooltipObject', null);
        });
      },
      /**
       * Zoom the Pannellum viewer to a tag's location
       * Handles both 2D pixel coordinates and 3D world coordinates
       * @param {Object} tag - Tag object with either centerPoint (2D) or point (3D)
       * @param {Object} image - 360 image object
       */
      zoomPannellumToTag(tag, image) {
        if (!tag || !this.pannellumViewerInstance) return;

        try {
          let yawPitch = null;

          // Option 1: Tag has 2D pixel coordinates
          if (
            (tag.centerPoint?.x != null || tag.imageXCenterCoordinate != null) &&
            image.width &&
            image.height
          ) {
            const x = tag.centerPoint?.x || tag.imageXCenterCoordinate;
            const y = tag.centerPoint?.y || tag.imageYCenterCoordinate;

            // Validate all parameters are valid numbers
            if (
              x != null &&
              y != null &&
              !isNaN(x) &&
              !isNaN(y) &&
              !isNaN(image.width) &&
              !isNaN(image.height)
            ) {
              yawPitch = this.pixelToYawPitchWrapper(x, y, image.width, image.height);
            } else {
              debugWarn('Invalid 2D pixel coordinates:', {
                x,
                y,
                width: image.width,
                height: image.height,
              });
            }
          }
          // Option 2: Tag has 3D world coordinates
          else if (tag.point?.x != null && tag.point?.y != null && tag.point?.z != null) {
            const THREE = window.THREE;
            const point3D = new THREE.Vector3(tag.point.x, tag.point.y, tag.point.z);
            yawPitch = this.convertPoint3DToYawPitch(point3D, image);

            if (!yawPitch) {
              debugWarn('Failed to convert 3D point to yaw/pitch:', tag.point);
            }
          } else {
            debugWarn('Tag has neither 2D pixel coordinates nor 3D world coordinates:', tag);
          }

          // Apply zoom if conversion was successful
          if (yawPitch) {
            this.pannellumViewerInstance.setYaw(yawPitch.yaw);
            this.pannellumViewerInstance.setPitch(yawPitch.pitch);
          }
        } catch (err) {
          debugError('Error zooming to tag:', err);
        }
      },
    },
    computed: {
      currentViewerIcon() {
        return this.getTabIcon(this.localViewerTab);
      },
      // Wraps $store.state.selectedImage access per CLAUDE.md rule #6 so the
      // PanoramaCompass overlay can react to scan switches via a prop.
      selectedImageId() {
        const img = this.$store.state.selectedImage;
        return img ? img.id : null;
      },
      isolatedInspects: {
        get() {
          return this.inspects != null ? this.inspects : this.$store.getters['AI_INSPECT/ISOLATED_INSPECTS'];
        },
        set(value) {
          if (this.inspects != null) {
            this.$emit('update:inspects', value);
            return;
          }
          this.$store.commit('AI_INSPECT/SET_ISOLATED_INSPECTS', value);
        },
      },
      isolatedTags() {
        return this.isolatedInspects.map((i) => i?.inspection).filter((i) => i?.name) ?? [];
      },
      forgeService() {
        return this.$store.state.forgeService;
      },
      pageView3d() {
        return this.$store.getters['GET_PAGE_VIEW_3D'];
      },
      viewer() {
        return this.$store.state.viewer;
      },
      planViewer() {
        return this.$store.state.planViewer;
      },
      pageView2d() {
        return this.$store.getters['GET_PAGE_VIEW_2D'];
      },
      facility: {
        get() {
          return this.$store.getters['FACILITY/GET_FACILITY'];
        },
      },
      localViewerTab: {
        get() {
          return this.viewerTab;
        },
        set(value) {
          this.$emit('update:viewerTab', value);
        },
      },
      inspectViewer: {
        get() {
          return this.$store.getters['AI_INSPECT/INSPECT_VIEWER'];
        },
        set(val) {
          this.$store.commit('AI_INSPECT/SET_INSPECT_VIEWER', val);
        },
      },
      noViewerAvailable() {
        return (
          !this.viewerState.isLoading &&
          ((this.localViewerTab === '2D View' && !this.planViewer) ||
            (this.localViewerTab === '3D View' && !this.viewer) ||
            (this.localViewerTab === '360 View' &&
              !this.inspectViewer &&
              !this.pannellumViewerInstance &&
              !this.isPanoramaViewerLoading))
        );
      },
      availableViewers() {
        let viewers = [];

        if (!this.hide360View && this.enabledTabs.includes('360 View')) {
          viewers.push('360 View');
        }

        // Availability flags are populated asynchronously. An already-mounted viewer or
        // the active tab is also authoritative, otherwise the entire selector can vanish
        // while its viewer remains visible (leaving only the empty tabs container).
        const has2dViewer = !!(
          this.$store.state.selectedLevel ||
          this.pageView2d ||
          this.planViewer ||
          this.localViewerTab === '2D View'
        );
        if (has2dViewer && this.enabledTabs.includes('2D View')) {
          viewers.push('2D View');
        }

        const has3dViewer = !!(
          this.$store.state.model3dAvailable ||
          this.$store.state.pointClouds?.length ||
          this.pageView3d ||
          this.viewer ||
          this.localViewerTab === '3D View'
        );
        if (has3dViewer && this.enabledTabs.includes('3D View')) {
          viewers.push('3D View');
        }

        // Add Document tab when:
        // - activeDocument is set, OR
        // - isolated tag has P&ID document
        // AND Document is in the enabled tabs list.
        if (
          (this.$store.state.TAGS?.activeDocument || this.isolatedTagHasPId) &&
          this.enabledTabs.includes('Document')
        ) {
          viewers.push('Document');
        }

        return viewers;
      },
      /**
       * Check if the currently isolated tag has an associated P&ID document
       */
      isolatedTagHasPId() {
        const isolatedTags = this.$store.getters['TAGS_REGISTER/GET_ISOLATED_TAGS'] ?? [];
        if (!isolatedTags.length) return false;

        const pIds = this.$store.getters['GET_P_IDS'] ?? [];
        const tagId = isolatedTags[0]?.id;

        return pIds.some((p) => p.forgeTagsIds?.includes(tagId));
      },
      /**
       * Get isolated tags from TAGS_REGISTER module for watching
       * (Note: different from `isolatedTags` which is for AI_INSPECT module)
       */
      tagsRegisterIsolatedTags() {
        return this.$store.getters['TAGS_REGISTER/GET_ISOLATED_TAGS'] ?? [];
      },
      isolatedInspections: {
        get() {
          return this.$store.getters['GET_ISOLATED_INSPECTIONS_IDS'];
        },
        set(value) {
          this.$store.commit('SET_ISOLATED_INSPECTIONS_IDS', value);
        },
      },
      isPanoramaViewerLoading() {
        return this.$store.getters['AI_INSPECT/IS_PANORAMA_VIEWER_LOADING'];
      },
      lastPanoramaPreviewImage() {
        return this.$store.getters['AI_INSPECT/LAST_PANORAMA_PREVIEW_IMAGE'];
      },
      is360PreviewImageLoaded: {
        get() {
          return this.$store.getters['AI_INSPECT/IS_360_PREVIEW_IMAGE_LOADED'];
        },
        set(value) {
          this.$store.commit('AI_INSPECT/SET_IS_360_PREVIEW_IMAGE_LOADED', value);
        },
      },
      viewerLoadingPercent: {
        get() {
          return this.$store.state.viewerState.percent;
        },
      },
    },
    watch: {
      localViewerTab: function (newVal, oldVal) {
        let viewerType = {
          '360 View': '360',
          '2D View': '2d',
          '3D View': '3d',
          Document: 'doc',
        };

        this.$store.commit('SET_VIEWER_TYPE', viewerType[newVal]);

        // Reset document ID tracking when switching AWAY from Document tab
        // This ensures a fresh viewer is created when returning to Document tab
        if (oldVal === 'Document' && newVal !== 'Document') {
          this.currentDocumentId = null;
        }

        // Notify consumers of tab changes so they can react (e.g. TaskBoard's
        // onTabChanged handler triggers nearby-360 resolution when entering
        // the 360 View tab). Emitted after Vuex state is consistent.
        this.$emit('tab-changed', newVal, oldVal);
      },
      isolatedInspects: {
        handler(newInspects) {
          if (!this.usePhotoSphereViewer) return;
          if (newInspects && newInspects.length > 0) {
            this.updatePhotoSphereViewer(newInspects);
          } else if (this.inspects != null) {
            // Report mode: an empty prop means "no areas left on this scan" — drop the markers, keep the panorama.
            this.currentInspects = [];
          }
        },
        deep: true,
      },
      // Watch for active document changes to auto-switch to Document tab
      '$store.state.TAGS.activeDocument': {
        handler(newDoc, oldDoc) {
          // Phase 2 amendment (TaskBoard viewer migration):
          // Only auto-switch to the Document tab if the consumer has it enabled.
          // Without this guard, consumers like Task (which uses
          // `enabledTabs: ['3D View','2D View','360 View']`) would silently move
          // `localViewerTab = 'Document'` even though Document isn't rendered,
          // leaving the UI in a confusing no-tab-selected state.
          if (newDoc && this.availableViewers.includes('Document')) {
            this.localViewerTab = 'Document';
            // Force display update after tab switch
            this.$nextTick(() => {
              this.displayViewer();
            });
          }
        },
      },
      // Clear activeDocument when isolated tag changes to avoid showing stale document
      // Note: Using computed property instead of getter path (Vue 2 can't watch bracket notation)
      tagsRegisterIsolatedTags: {
        handler(newTags, oldTags) {
          // Only clear if tags actually changed (not initial load)
          if (oldTags && oldTags.length > 0) {
            const oldTagId = oldTags[0]?.id;
            const newTagId = newTags?.[0]?.id;

            // If tag changed, clear the active document
            if (oldTagId !== newTagId) {
              this.$store.commit('SET_ACTIVE_DOCUMENT', null);
            }
          }
        },
        deep: true,
      },
    },
    created() {
      // Watch for isolated inspects changes
      if (this.usePhotoSphereViewer && this.isolatedInspects && this.isolatedInspects.length > 0) {
        this.updatePhotoSphereViewer(this.isolatedInspects);
      }
    },
    beforeDestroy() {
      // Round 6 fix: mirror the scan-switch cleanup in loadPannellumViewer so
      // the interaction-end timer and wheel listener don't leak after unmount.
      if (this.pannellumInteractEndTimer != null) {
        clearTimeout(this.pannellumInteractEndTimer);
        this.pannellumInteractEndTimer = null;
      }
      if (this.pannellumWheelHandler) {
        const container = document.getElementById('pannellum-viewer');
        if (container) {
          container.removeEventListener('wheel', this.pannellumWheelHandler);
        }
        this.pannellumWheelHandler = null;
      }
      this.pannellumNorthOffset = null;
      this.pannellumIsInteracting = false;

      // Cleanup Pannellum viewer instance
      if (this.pannellumViewerInstance) {
        this.pannellumViewerInstance.destroy();
        this.pannellumViewerInstance = null;
      }

      // Cleanup hotspot manager
      if (this.panoramaHotspotManager) {
        this.panoramaHotspotManager = null;
      }

      // Cleanup document viewer instance
      if (this.documentViewer) {
        this.documentViewer.finish();
        this.documentViewer = null;
      }

      // Clear PDF viewer initialization state (prevents stale guards)
      this.isInitializingViewer = false;
      this.currentDocumentId = null;

      // Clear active document from store
      if (this.$store.state.TAGS?.activeDocument) {
        this.$store.commit('SET_ACTIVE_DOCUMENT', null);
      }
    },
  };
</script>

<style lang="scss" scoped>
  // ===================================
  // Enhanced Viewer Header Styles
  // Industrial-Refined Aesthetic
  // ===================================

  .viewer-header {
    padding: 12px 16px;
    background: linear-gradient(
      180deg,
      rgba(var(--v-primary-base-rgb, 76, 175, 80), 0.03) 0%,
      transparent 100%
    );
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
    position: relative;

    &--fullscreen {
      padding: 16px 24px;
    }
  }

  .viewer-header__top {
    display: flex;
    align-items: center;
    gap: 16px;
  }

  .viewer-header__title {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-shrink: 0;
  }

  .viewer-header__icon-wrapper {
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(
      135deg,
      rgba(var(--v-primary-base-rgb, 76, 175, 80), 0.12) 0%,
      rgba(var(--v-primary-base-rgb, 76, 175, 80), 0.06) 100%
    );
    border-radius: 10px;
    border: 1px solid rgba(var(--v-primary-base-rgb, 76, 175, 80), 0.15);
    transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);

    &:hover {
      transform: scale(1.05);
      border-color: rgba(var(--v-primary-base-rgb, 76, 175, 80), 0.3);
    }
  }

  .viewer-header__title-text {
    font-size: 15px;
    font-weight: 600;
    color: rgba(0, 0, 0, 0.85);
    letter-spacing: -0.01em;

    .theme--dark & {
      color: rgba(255, 255, 255, 0.9);
    }
  }

  .viewer-header__tabs {
    display: flex;
    flex: 0 0 auto;
    align-items: center;
    gap: 6px;
    margin-left: auto;
    padding: 4px;
    background: rgba(0, 0, 0, 0.03);
    border-radius: 10px;
    border: 1px solid rgba(0, 0, 0, 0.04);

    .theme--dark & {
      background: rgba(255, 255, 255, 0.05);
      border-color: rgba(255, 255, 255, 0.08);
    }
  }

  .viewer-tab-btn {
    display: flex;
    flex: 0 0 auto;
    align-items: center;
    gap: 6px;
    padding: 8px 14px;
    border: none;
    background: transparent;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    position: relative;
    overflow: hidden;
    white-space: nowrap;

    &__icon {
      color: rgba(0, 0, 0, 0.5) !important;
      transition: all 0.2s ease;

      .theme--dark & {
        color: rgba(255, 255, 255, 0.5) !important;
      }
    }

    &__label {
      font-size: 13px;
      font-weight: 500;
      color: rgba(0, 0, 0, 0.6);
      letter-spacing: 0.02em;

      .theme--dark & {
        color: rgba(255, 255, 255, 0.6);
      }
    }

    &:hover:not(.viewer-tab-btn--active) {
      background: rgba(0, 0, 0, 0.04);

      .theme--dark & {
        background: rgba(255, 255, 255, 0.08);
      }

      .viewer-tab-btn__icon {
        color: rgba(0, 0, 0, 0.7) !important;

        .theme--dark & {
          color: rgba(255, 255, 255, 0.8) !important;
        }
      }

      .viewer-tab-btn__label {
        color: rgba(0, 0, 0, 0.8);

        .theme--dark & {
          color: rgba(255, 255, 255, 0.85);
        }
      }
    }

    &--active {
      background: var(--v-primary-base);
      box-shadow: 0 2px 8px rgba(var(--v-primary-base-rgb, 76, 175, 80), 0.25),
        0 1px 3px rgba(0, 0, 0, 0.1);

      .viewer-tab-btn__icon {
        color: white !important;
      }

      .viewer-tab-btn__label {
        color: white;
        font-weight: 600;
      }

      &::after {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: linear-gradient(180deg, rgba(255, 255, 255, 0.15) 0%, transparent 50%);
        pointer-events: none;
      }
    }
  }

  // Window Controls (Expand + Close) - Windows-style layout
  .viewer-header__controls {
    display: flex;
    align-items: center;
    gap: 2px;
    margin-left: 12px;
    padding: 2px;
    background: rgba(0, 0, 0, 0.03);
    border-radius: 8px;
    border: 1px solid rgba(0, 0, 0, 0.04);

    .theme--dark & {
      background: rgba(255, 255, 255, 0.05);
      border-color: rgba(255, 255, 255, 0.08);
    }
  }

  .viewer-control-btn {
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: none;
    background: transparent;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.15s cubic-bezier(0.4, 0, 0.2, 1);

    .v-icon {
      color: rgba(0, 0, 0, 0.5) !important;
      transition: all 0.15s ease;

      .theme--dark & {
        color: rgba(255, 255, 255, 0.5) !important;
      }
    }

    &:active:not(:disabled) {
      transform: scale(0.92);
    }

    &:disabled {
      opacity: 0.35;
      cursor: not-allowed;
      pointer-events: none;
    }

    // Expand button
    &--expand {
      &:hover {
        background: rgba(var(--v-primary-base-rgb, 76, 175, 80), 0.15);

        .v-icon {
          color: var(--v-primary-base) !important;
        }
      }
    }

    // Close button
    &--close {
      &:hover {
        background: var(--v-error-base);

        .v-icon {
          color: white !important;
        }
      }
    }
  }

  .viewer-header__bottom {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-top: 10px;
    padding-top: 10px;
    border-top: 1px dashed rgba(0, 0, 0, 0.06);

    .theme--dark & {
      border-top-color: rgba(255, 255, 255, 0.08);
    }
  }

  .viewer-tags-btn {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 5px 12px 5px 5px;
    background: linear-gradient(
      135deg,
      rgba(var(--v-primary-base-rgb, 76, 175, 80), 0.08) 0%,
      rgba(var(--v-primary-base-rgb, 76, 175, 80), 0.03) 100%
    );
    border: 1px solid rgba(var(--v-primary-base-rgb, 76, 175, 80), 0.15);
    border-radius: 20px;
    cursor: pointer;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);

    &:hover {
      background: rgba(var(--v-primary-base-rgb, 76, 175, 80), 0.12);
      border-color: rgba(var(--v-primary-base-rgb, 76, 175, 80), 0.3);
      transform: translateY(-1px);
      box-shadow: 0 2px 8px rgba(var(--v-primary-base-rgb, 76, 175, 80), 0.12);
    }

    &__count {
      display: flex;
      align-items: center;
      justify-content: center;
      min-width: 22px;
      height: 22px;
      padding: 0 6px;
      background: var(--v-primary-base);
      color: white;
      font-size: 11px;
      font-weight: 700;
      border-radius: 11px;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
    }

    &__label {
      font-size: 12px;
      font-weight: 500;
      color: var(--v-primary-base);
      letter-spacing: -0.01em;
    }
  }

  // ===================================
  // Original Component Styles
  // ===================================

  #viewersSection {
    border: 1px solid rgba(0, 0, 0, 0.08);
    border-radius: 8px;

    .theme--dark & {
      border-color: rgba(255, 255, 255, 0.12);
    }

    .v-card {
      border-radius: 8px;
    }
  }

  #forgeViewer canvas {
    max-height: calc(100vh - 300px);
  }

  #planViewer {
    height: 100%;
    width: 100%;
  }

  #viewersSection {
    & .loading-section {
      position: absolute;
      display: flex;
      z-index: 9;
      top: 75px;
      left: 0;
      width: 100%;
      height: calc(100% - 80px);
    }

    & .custom-overlay {
      height: -webkit-fill-available;
      backdrop-filter: saturate(100%) blur(3px) !important;
      top: auto;
      bottom: auto;
      margin: 0px 8px 8px;
    }
  }

  #panorama-viewer-tab {
    .white-overlay {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: #fff;
    }

    .glass-overlay {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      backdrop-filter: blur(4px);
      background: rgb(94 94 94 / 20%);
      border: 1px solid rgba(255, 255, 255, 0.2);
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.2);
      pointer-events: none;
      transition: all 1s ease-in;
    }

    .overlay-hidden .glass-overlay {
      background: rgb(94 94 94 / 0%);
    }

    .panorama-overlay {
      position: absolute;
      width: 100%;
      height: -webkit-fill-available;
      transform-origin: center center;
      transition: all 1s cubic-bezier(0.4, 0, 0.2, 1);
      opacity: 1;
      z-index: 1;
      visibility: visible;
    }

    .panorama-overlay.overlay-hidden {
      opacity: 0;
      visibility: hidden;
    }

    .preview-image {
      width: 100%;
      height: 100%;
      object-fit: cover;
      transition: opacity 1s cubic-bezier(0.4, 0, 0.2, 1);
    }

    .overlay-hidden .preview-image {
      opacity: 0;
    }
  }

  // ===================================
  // Document Viewer & PDF Pagination
  // ===================================

  .document-viewer-wrapper {
    position: relative;
  }

  .pdf-pagination-bar {
    position: absolute;
    bottom: 16px;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px 14px;
    background: rgba(255, 255, 255, 0.95);
    border-radius: 24px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.12), 0 1px 4px rgba(0, 0, 0, 0.08);
    z-index: 10;
    backdrop-filter: blur(8px);
    border: 1px solid rgba(0, 0, 0, 0.06);
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);

    &:hover {
      box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15), 0 2px 6px rgba(0, 0, 0, 0.1);
    }

    .page-indicator {
      font-size: 13px;
      font-weight: 600;
      min-width: 56px;
      text-align: center;
      color: rgba(0, 0, 0, 0.85);
      letter-spacing: -0.01em;
      user-select: none;
    }

    .pagination-btn {
      transition: all 0.15s ease;

      &:not(:disabled):hover {
        background: rgba(var(--v-primary-base-rgb, 76, 175, 80), 0.12) !important;

        .v-icon {
          color: var(--v-primary-base) !important;
        }
      }

      &:disabled {
        opacity: 0.35;
      }

      .v-icon {
        color: rgba(0, 0, 0, 0.6) !important;
        transition: color 0.15s ease;
      }
    }

    // Dark theme support
    .theme--dark & {
      background: rgba(30, 35, 40, 0.95);
      border-color: rgba(255, 255, 255, 0.1);

      .page-indicator {
        color: rgba(255, 255, 255, 0.9);
      }

      .pagination-btn {
        .v-icon {
          color: rgba(255, 255, 255, 0.7) !important;
        }

        &:not(:disabled):hover {
          background: rgba(var(--v-primary-base-rgb, 76, 175, 80), 0.2) !important;

          .v-icon {
            color: var(--v-primary-lighten2) !important;
          }
        }
      }
    }
  }

  // ===================================
  // Custom Dashboard industrial chrome
  // ===================================
  #viewersSection.viewers-section--industrial {
    --viewer-industrial-surface: #f2f4f3;
    --viewer-industrial-surface-raised: #f8faf9;
    --viewer-industrial-surface-pressed: #f0f2f1;
    --viewer-industrial-border: #d7ddda;
    --viewer-industrial-border-strong: #bdc7c2;
    --viewer-industrial-ink: #29312e;
    --viewer-industrial-muted: #66736e;
    --viewer-industrial-viewport: #202523;

    overflow: hidden;
    background: var(--viewer-industrial-surface);
    border-color: var(--viewer-industrial-border);
    border-radius: 6px;
    box-shadow: 0 1px 2px rgba(18, 33, 28, 0.12);

    > .v-card {
      background: var(--viewer-industrial-surface);
      border-radius: 5px;
    }

    > .v-card > .v-divider {
      display: none;
    }

    .viewer-header {
      min-height: 54px;
      padding: 8px 10px;
      background: var(--viewer-industrial-surface-raised);
      border-bottom: 1px solid var(--viewer-industrial-border-strong);
      box-shadow: inset 0 -1px 0 rgba(41, 49, 46, 0.06);

      &::before {
        display: none;
      }

      &--fullscreen {
        padding: 10px 14px;
      }
    }

    .viewer-header__top {
      gap: 10px;
    }

    .viewer-header__title {
      gap: 8px;
      min-width: 112px;
    }

    .viewer-header__icon-wrapper {
      width: 30px;
      height: 30px;
      background: rgba(3, 98, 76, 0.08);
      border-color: rgba(3, 98, 76, 0.25);
      border-radius: 4px;
      transition: border-color 0.15s ease-out, background-color 0.15s ease-out;

      &:hover {
        transform: none;
        background: rgba(3, 98, 76, 0.12);
        border-color: rgba(3, 98, 76, 0.42);
      }
    }

    .viewer-header__title-text {
      color: var(--viewer-industrial-ink);
      font-family: var(--ds-font-family-body, "IBM Plex Sans", sans-serif);
      font-size: 13px;
      font-weight: 700;
      letter-spacing: 0.015em;
    }

    .viewer-header__tabs {
      gap: 2px;
      padding: 2px;
      background: var(--viewer-industrial-surface-pressed);
      border-color: var(--viewer-industrial-border);
      border-radius: 5px;
      box-shadow: inset 0 1px 2px rgba(18, 33, 28, 0.08);
    }

    .viewer-tab-btn {
      min-height: 30px;
      gap: 5px;
      padding: 6px 10px;
      border-radius: 3px;
      transition: background-color 0.15s ease-out, color 0.15s ease-out;

      &:not(.viewer-tab-btn--active) {
        background: #f7f8f7;
      }

      &__icon {
        color: var(--viewer-industrial-muted) !important;
      }

      &__label {
        color: var(--viewer-industrial-muted);
        font-size: 11px;
        font-weight: 600;
        letter-spacing: 0.04em;
      }

      &:hover:not(.viewer-tab-btn--active) {
        background: #ecefed;

        .viewer-tab-btn__icon,
        .viewer-tab-btn__label {
          color: var(--viewer-industrial-ink) !important;
        }
      }

      &:focus-visible {
        outline: 2px solid var(--v-primary-base);
        outline-offset: 1px;
      }

      &--active {
        background: var(--v-primary-base);
        box-shadow: none;

        .viewer-tab-btn__icon,
        .viewer-tab-btn__label {
          color: #f4f8f6 !important;
        }

        &::after {
          display: none;
        }
      }
    }

    .viewer-header__controls {
      gap: 4px;
      margin-left: 4px;
      padding: 0;
      background: transparent;
      border: 0;
      border-radius: 0;
    }

    .viewer-control-btn {
      width: 30px;
      height: 30px;
      background: #f7f8f7;
      border: 1px solid var(--viewer-industrial-border);
      border-radius: 4px;

      .v-icon {
        color: var(--viewer-industrial-muted) !important;
      }

      &:active:not(:disabled) {
        transform: none;
        background: var(--viewer-industrial-surface-pressed);
      }

      &:focus-visible {
        outline: 2px solid var(--v-primary-base);
        outline-offset: 1px;
      }

      &--expand:hover {
        background: rgba(3, 98, 76, 0.09);
        border-color: var(--v-primary-base);
      }

      &--close:hover {
        background: rgba(183, 28, 28, 0.09);
        border-color: var(--v-error-base);

        .v-icon {
          color: var(--v-error-base) !important;
        }
      }
    }

    .viewer-header__bottom {
      margin-top: 7px;
      padding-top: 7px;
      border-top: 1px solid var(--viewer-industrial-border);
    }

    .viewer-tags-btn {
      padding: 4px 9px 4px 4px;
      background: var(--viewer-industrial-surface-pressed);
      border-color: var(--viewer-industrial-border);
      border-radius: 4px;
      box-shadow: none;

      &:hover {
        transform: none;
        background: rgba(3, 98, 76, 0.09);
        border-color: var(--v-primary-base);
        box-shadow: none;
      }
    }

    .viewer-viewport {
      margin: 8px !important;
      background: var(--viewer-industrial-viewport);
      border: 1px solid var(--viewer-industrial-border-strong);
      border-radius: 4px !important;
      box-shadow: inset 0 0 0 1px rgba(238, 241, 239, 0.05);
    }

    #viewer3d,
    #docViewerEmbedded {
      border-radius: 3px !important;
    }
  }

  #viewersSection.viewers-section--industrial.theme--dark {
    --viewer-industrial-surface: #252b29;
    --viewer-industrial-surface-raised: #2c3330;
    --viewer-industrial-surface-pressed: #1f2523;
    --viewer-industrial-border: #46514d;
    --viewer-industrial-border-strong: #6e7d77;
    --viewer-industrial-ink: #edf3f0;
    --viewer-industrial-muted: #a9b7b1;
    --viewer-industrial-viewport: #151a18;

    .viewer-header__icon-wrapper {
      background: rgba(44, 194, 149, 0.1);
      border-color: rgba(44, 194, 149, 0.32);
    }

    .viewer-tab-btn--active {
      background: var(--v-secondary-base);

      .viewer-tab-btn__icon,
      .viewer-tab-btn__label {
        color: #10231d !important;
      }
    }
  }
</style>
